import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Phone, Mail, MessageSquare, CheckCircle2, X, RefreshCw,
  TrendingUp, AlertCircle, Clock, UserCheck, UserX, ExternalLink,
  Trash2, ChevronDown, ChevronRight, Sparkles,
} from "lucide-react";
import { PageHeader, EmptyState } from "../components/ui/kaizen";

const STATUS_CONFIG = {
  new: { label: "Nouveau", color: "bg-blue-500/20 text-blue-300 border-blue-500/40", icon: Sparkles },
  awaiting_callback: { label: "À rappeler", color: "bg-orange-500/20 text-orange-300 border-orange-500/40", icon: Clock },
  contacted: { label: "Contacté", color: "bg-amber-500/20 text-amber-300 border-amber-500/40", icon: MessageSquare },
  converted: { label: "Converti ✓", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", icon: UserCheck },
  lost: { label: "Perdu", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/40", icon: UserX },
};

const PROGRAM_LABEL = {
  "muay-thai": "Muay Thaï",
  "bjj": "Jiu-Jitsu",
  "essai-double": "Double essai",
  "kids": "Enfants",
  "autre": "Autre",
};

const PRIORITY_COLOR = {
  high: "bg-red-500/20 text-red-300 border-red-500/40",
  medium: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  low: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
};

export default function CRMLeadsModule() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [notesById, setNotesById] = useState({});
  const [emailLead, setEmailLead] = useState(null);
  const [senderInfo, setSenderInfo] = useState(null);

  useEffect(() => { api.get("/leads/email-sender").then((r) => setSenderInfo(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filter !== "all") qs.set("status", filter);
      if (sourceFilter !== "all") qs.set("source", sourceFilter);
      const params = qs.toString() ? `?${qs.toString()}` : "";
      const [l, s, src, prg] = await Promise.all([
        api.get(`/leads${params}`),
        api.get("/leads/stats"),
        api.get("/leads/sources"),
        api.get("/leads/programs-stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
      setSources(src.data.sources || []);
      setPrograms(prg.data.programs || []);
    } catch { toast.error("Erreur chargement"); }
    setLoading(false);
  }, [filter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const [followupModal, setFollowupModal] = useState(null);

  const runAutoFollowup = async () => {
    try {
      const [{ data: d1 }, { data: d2 }, { data: t1 }, { data: t2 }] = await Promise.all([
        api.post("/leads/auto-followup?dry_run=true&wave=1"),
        api.post("/leads/auto-followup?dry_run=true&wave=2"),
        api.get("/leads/followup-template?wave=1"),
        api.get("/leads/followup-template?wave=2"),
      ]);
      if (!d1.count && !d2.count) { toast.info("Aucun lead à relancer pour le moment."); return; }
      setFollowupModal({
        wave: d1.count ? 1 : 2, count1: d1.count, count2: d2.count, sending: false,
        tpl: { 1: { subject: t1.subject, body: t1.body }, 2: { subject: t2.subject, body: t2.body } },
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur relance"); }
  };

  const updateStatus = async (id, status, notes) => {
    try {
      const body = { status };
      if (notes) body.notes = notes;
      await api.put(`/leads/${id}/status`, body);
      toast.success(`Statut: ${STATUS_CONFIG[status]?.label || status}`);
      await load();
    } catch { toast.error("Erreur"); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Supprimer ce lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead supprimé");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const conversionRate = stats?.total > 0 ? ((stats.converted / stats.total) * 100).toFixed(0) : 0;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="crm-leads-module">
      <PageHeader
        title="CRM Leads"
        icon={Users}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Leads" }]}
        subtitle={`Pipeline de conversion · ${stats?.total || 0} leads total · ${conversionRate}% conversion`}
        actions={
          <button data-testid="crm-leads-refresh" onClick={load} className="px-4 py-2 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Actualiser
          </button>
        }
      />

      {senderInfo && (!senderInfo.connected || senderInfo.needs_reconnect) && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/40 flex items-center justify-between gap-3 flex-wrap" data-testid="crm-gmail-banner">
          <div className="text-sm text-red-200">
            ⚠️ Gmail {senderInfo.connected ? "déconnecté" : "non connecté"} ({senderInfo.company || "impactkb.bjj@gmail.com"}) — impossible d'envoyer des courriels aux leads tant que le compte n'est pas reconnecté.
          </div>
          <button
            data-testid="crm-page-reconnect-gmail-btn"
            onClick={async () => {
              try {
                const { data } = await api.get("/integrations/gmail/auth-url");
                window.location.href = data.auth_url;
              } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'ouvrir la reconnexion Gmail"); }
            }}
            className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-bold flex items-center gap-2 shrink-0">
            <Mail className="w-4 h-4" /> Reconnecter Gmail
          </button>
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats?.total || 0} color="text-white" icon={Users} />
        <StatCard label="Nouveaux" value={stats?.new || 0} color="text-blue-300" icon={Sparkles} pulse={(stats?.new || 0) > 0} />
        <StatCard label="Contactés" value={stats?.contacted || 0} color="text-amber-300" icon={MessageSquare} />
        <StatCard label="Convertis" value={stats?.converted || 0} color="text-emerald-300" icon={CheckCircle2} />
        <StatCard label="Taux conv." value={`${conversionRate}%`} color="text-cyan-300" icon={TrendingUp} />
      </div>

      {(stats?.overdue_48h || 0) > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-center justify-between gap-3 flex-wrap" data-testid="crm-recontact-badge">
          <div className="text-sm text-amber-200">
            🔔 <b>{stats.overdue_48h}</b> lead(s) à recontacter — aucune réponse depuis plus de 48h
          </div>
          <button data-testid="crm-auto-followup-btn" onClick={runAutoFollowup}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold flex items-center gap-2 shrink-0">
            <Mail className="w-4 h-4" /> Relancer automatiquement
          </button>
        </div>
      )}

      {/* SOURCES & PROGRAMMES */}
      {(sources.length > 0 || programs.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="leads-sources-section">
          <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
            <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-cyan-400" /> Performance par source
            </h3>
            {sources.length === 0 ? (
              <p className="text-xs text-zinc-500">Aucune donnée — les leads à venir afficheront leur page d'origine ici.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-zinc-500">
                  <tr><th className="text-left py-1">Source</th><th className="text-right">Total</th><th className="text-right">Convertis</th><th className="text-right">Taux</th></tr>
                </thead>
                <tbody>
                  {sources.map((s, i) => (
                    <tr key={s.source || `src-${i}`} className="border-t border-zinc-800/60" data-testid={`source-row-${s.source}`}>
                      <td className="py-1.5 text-zinc-200 truncate max-w-[160px]">{s.source}</td>
                      <td className="text-right text-zinc-300">{s.total}</td>
                      <td className="text-right text-emerald-300">{s.converted}</td>
                      <td className="text-right font-semibold text-cyan-300">{s.conversion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
            <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" /> Programmes les plus demandés
            </h3>
            {programs.length === 0 ? (
              <p className="text-xs text-zinc-500">Aucune donnée.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-zinc-500">
                  <tr><th className="text-left py-1">Programme</th><th className="text-right">Total</th><th className="text-right">Convertis</th><th className="text-right">Taux</th></tr>
                </thead>
                <tbody>
                  {programs.map((p, i) => (
                    <tr key={p.program || `prg-${i}`} className="border-t border-zinc-800/60" data-testid={`program-row-${p.program}`}>
                      <td className="py-1.5 text-zinc-200">{PROGRAM_LABEL[p.program] || p.program}</td>
                      <td className="text-right text-zinc-300">{p.total}</td>
                      <td className="text-right text-emerald-300">{p.converted}</td>
                      <td className="text-right font-semibold text-amber-300">{p.conversion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* FILTRES */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="status-filters">
        {[
          { id: "all", label: "Tous" },
          { id: "new", label: "Nouveaux" },
          { id: "awaiting_callback", label: "À rappeler" },
          { id: "contacted", label: "Contactés" },
          { id: "converted", label: "Convertis" },
          { id: "lost", label: "Perdus" },
        ].map(f => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded text-xs font-semibold ${
              filter === f.id
                ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-200"
                : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white"
            }`}
          >{f.label}</button>
        ))}
      </div>

      {/* FILTRE SOURCE (dont vue « Leads Admission ») */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="source-filters">
        <button data-testid="source-filter-all" onClick={() => setSourceFilter("all")}
          className={`px-3 py-1.5 rounded text-xs font-semibold ${sourceFilter === "all" ? "bg-amber-500/20 border border-amber-500/40 text-amber-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white"}`}>Toutes sources</button>
        <button data-testid="source-filter-admission" onClick={() => setSourceFilter("Admission (qualif.)")}
          className={`px-3 py-1.5 rounded text-xs font-semibold ${sourceFilter === "Admission (qualif.)" ? "bg-amber-500/20 border border-amber-500/40 text-amber-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white"}`}>🎯 Leads Admission</button>
        {sources.filter((s) => s.source && s.source !== "Admission (qualif.)").map((s) => (
          <button key={s.source} onClick={() => setSourceFilter(s.source)}
            className={`px-3 py-1.5 rounded text-xs font-semibold ${sourceFilter === s.source ? "bg-amber-500/20 border border-amber-500/40 text-amber-200" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white"}`}>{s.source}</button>
        ))}
      </div>

      {/* LISTE */}
      {loading ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-8 text-center text-zinc-500 text-sm">Chargement...</div>
      ) : leads.length === 0 ? (
        <EmptyState
          icon={Users}
          title={filter === "all" ? "Aucun lead pour l'instant" : "Aucun lead avec ce statut"}
          description={filter === "all"
            ? "Partage le lien du cours d'essai (19,99 $) sur tes réseaux pour commencer à générer des leads."
            : "Change de filtre pour voir d'autres leads, ou réinitialise sur « Tous »."}
          {...(filter === "all" ? { actionLabel: "Ouvrir la page d'essai", onAction: () => window.open("/cours-essai", "_blank") } : {})}
        />
      ) : (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 overflow-hidden">
          <div className="divide-y divide-zinc-800">
            {leads.map((lead, i) => {
              const sc = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
              const SI = sc.icon;
              const isOpen = expandedId === lead.id;
              const cleanPhone = (lead.phone || "").replace(/[^\d+]/g, "");
              return (
                <div key={lead.id || `lead-${i}`} className="p-4 hover:bg-zinc-900/40 transition" data-testid={`lead-${lead.id}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => setExpandedId(isOpen ? null : lead.id)} className="mt-1 text-zinc-500 hover:text-white">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-semibold text-white">{lead.name}</span>
                        {lead.child_name && (
                          <span className="text-xs text-zinc-400">
                            · enfant {lead.child_name}{lead.child_age && ` (${lead.child_age} ans)`}
                          </span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${sc.color} flex items-center gap-1`}>
                          <SI className="w-3 h-3" /> {sc.label}
                        </span>
                        {lead.priority && (
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${PRIORITY_COLOR[lead.priority] || ""}`}>
                            {lead.priority}
                          </span>
                        )}
                        <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">
                          {PROGRAM_LABEL[lead.program] || lead.program}
                        </span>
                        {lead.preferred_date && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-violet-500/15 text-violet-200 border border-violet-500/30 flex items-center gap-1" data-testid={`preferred-date-${lead.id}`}>
                            <Clock className="w-3 h-3" /> Souhaitée: {lead.preferred_date}
                          </span>
                        )}
                        {lead.auto_followup_at && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/25 flex items-center gap-1" data-testid={`relaunched-${lead.id}`}>
                            <Mail className="w-3 h-3" /> Relancé {new Date(lead.auto_followup_at).toLocaleDateString("fr-CA", { day: "numeric", month: "short" })}
                          </span>
                        )}
                        <span className="text-xs text-zinc-500 ml-auto">
                          {new Date(lead.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setEmailLead(lead); }} className="text-zinc-400 hover:text-cyan-300 flex items-center gap-1" data-testid={`email-${lead.id}`} title="Écrire via impactkb.bjj@gmail.com">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </button>
                        {lead.phone && (
                          <a href={`tel:${cleanPhone}`} className="text-zinc-400 hover:text-orange-300 flex items-center gap-1" data-testid={`phone-${lead.id}`}>
                            <Phone className="w-3 h-3" /> {lead.phone}
                          </a>
                        )}
                        <span className="text-zinc-600">Source: {lead.source || "—"}</span>
                      </div>
                      {lead.message && !isOpen && (
                        <div className="mt-2 text-xs text-zinc-400 italic line-clamp-1">"{lead.message}"</div>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 ml-7 space-y-3" data-testid={`lead-details-${lead.id}`}>
                      {lead.message && (
                        <div className="p-3 rounded bg-zinc-900 border border-zinc-800">
                          <div className="text-xs text-zinc-500 mb-1">Message du lead</div>
                          <div className="text-sm text-zinc-200">{lead.message}</div>
                        </div>
                      )}
                      {lead.notes && (
                        <div className="p-3 rounded bg-amber-500/5 border border-amber-500/20">
                          <div className="text-xs text-amber-300 mb-1">Notes internes</div>
                          <div className="text-sm text-zinc-200 whitespace-pre-wrap">{lead.notes}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-xs text-zinc-500 mb-2">Ajouter une note (optionnel)</div>
                        <textarea
                          data-testid={`notes-${lead.id}`}
                          rows={2}
                          placeholder="Notes sur ton appel / rencontre / progression..."
                          value={notesById[lead.id] || ""}
                          onChange={e => setNotesById({ ...notesById, [lead.id]: e.target.value })}
                          className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          data-testid={`mark-contacted-${lead.id}`}
                          onClick={() => updateStatus(lead.id, "contacted", notesById[lead.id])}
                          className="px-3 py-1.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-xs flex items-center gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5" /> Marquer Contacté
                        </button>
                        <button
                          data-testid={`mark-converted-${lead.id}`}
                          onClick={() => updateStatus(lead.id, "converted", notesById[lead.id])}
                          className="px-3 py-1.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-xs flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Converti
                        </button>
                        <button
                          data-testid={`mark-lost-${lead.id}`}
                          onClick={() => updateStatus(lead.id, "lost", notesById[lead.id])}
                          className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs flex items-center gap-1.5">
                          <UserX className="w-3.5 h-3.5" /> Perdu
                        </button>
                        {lead.phone && (
                          <a
                            href={`tel:${cleanPhone}`}
                            data-testid={`call-now-${lead.id}`}
                            className="px-3 py-1.5 rounded bg-orange-500 hover:bg-orange-400 text-white text-xs font-semibold flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5" /> Appeler maintenant
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => setEmailLead(lead)}
                          data-testid={`email-now-${lead.id}`}
                          className="px-3 py-1.5 rounded bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-semibold flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5" /> Envoyer courriel
                        </button>
                        <button
                          data-testid={`delete-${lead.id}`}
                          onClick={() => deleteLead(lead.id)}
                          className="px-3 py-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 text-xs flex items-center gap-1.5 ml-auto">
                          <Trash2 className="w-3.5 h-3.5" /> Supprimer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        )}

      {/* QUICK LINK */}
      <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/5 border border-amber-500/30">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-bold text-white mb-1">Génère plus de leads</h3>
            <p className="text-xs text-zinc-400">Partage ce lien sur Facebook, Instagram, WordPress, ou imprime-le sur tes flyers.</p>
          </div>
          <a
            href="/cours-essai"
            target="_blank"
            rel="noreferrer"
            className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Ouvrir la page d'essai
          </a>
        </div>
      </div>
      {emailLead && (
        <EmailModal lead={emailLead} sender={senderInfo}
          onClose={() => setEmailLead(null)}
          onSent={() => { setEmailLead(null); load(); }} />
      )}

      {followupModal && (() => {
        const w = followupModal.wave;
        const cur = followupModal.tpl[w];
        const count = w === 1 ? followupModal.count1 : followupModal.count2;
        const setCur = (patch) => setFollowupModal((m) => ({ ...m, tpl: { ...m.tpl, [w]: { ...m.tpl[w], ...patch } } }));
        return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setFollowupModal(null)} data-testid="followup-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-950 p-5 space-y-3">
            <h3 className="text-lg font-bold text-amber-300">✉️ Relance automatique — aperçu</h3>
            <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
              <button data-testid="followup-wave-1" onClick={() => setFollowupModal((m) => ({ ...m, wave: 1 }))}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm ${w === 1 ? "bg-amber-600 text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
                1re relance · 48h ({followupModal.count1})
              </button>
              <button data-testid="followup-wave-2" onClick={() => setFollowupModal((m) => ({ ...m, wave: 2 }))}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm ${w === 2 ? "bg-amber-600 text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
                2e vague · 5 jours ({followupModal.count2})
              </button>
            </div>
            <p className="text-xs text-zinc-400">
              Envoyée à <b>{count}</b> lead(s) via impactkb.bjj@gmail.com. Utilise <code className="text-amber-300">{"{name}"}</code> pour le prénom.
            </p>
            <label className="block">
              <span className="text-xs text-zinc-400">Objet</span>
              <input data-testid="followup-subject" value={cur.subject}
                onChange={(e) => setCur({ subject: e.target.value })}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Message</span>
              <textarea data-testid="followup-body" rows={7} value={cur.body}
                onChange={(e) => setCur({ body: e.target.value })}
                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono" />
            </label>
            <div className="flex items-center justify-between gap-2 pt-1">
              <button data-testid="followup-save-template"
                onClick={async () => {
                  try {
                    await api.put(`/leads/followup-template?wave=${w}`, { subject: cur.subject, body: cur.body });
                    toast.success("Modèle enregistré ✓");
                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                }}
                className="text-xs text-zinc-400 hover:text-amber-300 underline">💾 Enregistrer le modèle</button>
              <div className="flex gap-2">
                <button onClick={() => setFollowupModal(null)} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm">Annuler</button>
                <button data-testid="followup-send-btn" disabled={followupModal.sending || !count}
                  onClick={async () => {
                    setFollowupModal((m) => ({ ...m, sending: true }));
                    try {
                      await api.put(`/leads/followup-template?wave=${w}`, { subject: cur.subject, body: cur.body });
                      const res = await api.post(`/leads/auto-followup?dry_run=false&wave=${w}`);
                      if (res.data.sent) toast.success(`✉️ ${res.data.sent} relance(s) envoyée(s)`);
                      else toast.error(res.data.reason || "Aucune relance envoyée (Gmail non connecté ?)");
                      setFollowupModal(null);
                      load();
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi"); setFollowupModal((m) => ({ ...m, sending: false })); }
                  }}
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-sm font-bold disabled:opacity-40">
                  ✉️ Envoyer à {count} lead(s)
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function EmailModal({ lead, sender, onClose, onSent }) {
  const [subject, setSubject] = useState(`Institut Impact Élite Martial — suite à ta demande`);
  const [body, setBody] = useState(
    `Bonjour ${lead.name || ""},\n\nMerci pour ton intérêt envers l'Institut Impact Élite Martial ! ` +
    `Je te contacte suite à ta demande${lead.program ? ` pour ${lead.program}` : ""}.\n\n` +
    `✅ Ton cours d'essai (payé) est confirmé — présente-toi 15 min à l'avance, en tenue de sport avec une bouteille d'eau (aucun équipement requis).\n\n` +
    `N'hésite pas à me répondre pour choisir ton créneau. 🥋\n\nAu plaisir,\nL'équipe Kaizen`);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const canSend = sender?.connected && !sender?.needs_reconnect;
  const reconnectGmail = async () => {
    try {
      const { data } = await api.get("/integrations/gmail/auth-url");
      window.location.href = data.auth_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'ouvrir la reconnexion Gmail"); }
  };

  useEffect(() => { api.get("/leads/email-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {}); }, []);

  const fill = (val) => (val || "").replace(/{name}/g, lead.name || "").replace(/{program}/g, lead.program ? ` pour ${lead.program}` : "");
  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(fill(t.subject));
    setBody(fill(t.body));
  };
  const saveAsTemplate = async () => {
    const name = window.prompt("Nom du modèle :");
    if (!name) return;
    try {
      const r = await api.post("/leads/email-templates", { name, subject, body });
      setTemplates((t) => [r.data, ...t]);
      toast.success("Modèle enregistré");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Sujet et message requis");
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead.id}/email`, { subject, body });
      toast.success(`Courriel envoyé à ${lead.email} depuis ${r.data.from || "Gmail"}`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-zinc-900 border border-zinc-800 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="crm-email-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-zinc-50 flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-400" /> Écrire à {lead.name}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-zinc-400 flex flex-wrap gap-x-4 gap-y-1">
            <span>À : <span className="text-zinc-200">{lead.email}</span></span>
            <span>De : <span className="text-cyan-300" data-testid="crm-email-from">{sender?.sender_email || "compte Gmail non connecté"}</span></span>
          </div>
          {!canSend && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs space-y-2" data-testid="crm-email-warning">
              <div>{sender?.connected
                ? "L'autorisation Google a expiré. Reconnecte le compte Gmail de la compagnie pour envoyer."
                : "Aucun compte Gmail connecté. Connecte impactkb.bjj@gmail.com pour envoyer depuis le courriel de la compagnie."}</div>
              <button type="button" onClick={reconnectGmail} data-testid="crm-reconnect-gmail-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white font-semibold">
                <Mail className="w-3.5 h-3.5" /> Reconnecter Gmail
              </button>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Modèle</label>
            <select data-testid="crm-email-template-select" defaultValue="" onChange={(e) => applyTemplate(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white focus:border-cyan-500 outline-none">
              <option value="">— Choisir un modèle —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.builtin ? "" : " ✎"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Sujet</label>
            <input data-testid="crm-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white focus:border-cyan-500 outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Message</label>
            <textarea data-testid="crm-email-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white focus:border-cyan-500 outline-none" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <button data-testid="crm-save-template-btn" onClick={saveAsTemplate} type="button"
              className="text-xs text-zinc-400 hover:text-cyan-300 underline">Enregistrer comme modèle</button>
          </div>
          <button data-testid="crm-email-send-btn" onClick={send} disabled={busy || !canSend}
            className="w-full py-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            <Mail className="w-4 h-4" /> {busy ? "Envoi…" : "Envoyer le courriel"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, pulse }) {
  return (
    <div className={`p-4 rounded-lg border ${pulse ? "border-blue-500/50 bg-blue-500/10 animate-pulse" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-zinc-500">{label}</div>
          <div className={`text-2xl font-bold ${color} mt-1`}>{value}</div>
        </div>
        <Icon className="w-5 h-5 text-zinc-600" />
      </div>
    </div>
  );
}
